<template>
  <v-app :class="{ isModal }" id="supportDocsApp">
    <v-main>
      <SystemMessage :systemMessage="systemMessage" />
      <v-container v-if="haveSupportDocsLoaded" fluid pb-4 style="background: white">
        <div v-if="isModal" class="closeButton" @click="handleCloseClick">X</div>
        <v-row class="ml-4 mr-4">
          <v-col>
            <h1 class="mb-2 text-center">Support</h1>
          </v-col>
        </v-row>
        <v-row>
          <!-- support docs -->
          <v-col cols="12">
            <v-row v-if="currentlyViewedSupportDoc" justify="center">
              <v-col>
                <v-row>
                  <v-col>
                    <v-btn @click="handleBackClick">BACK</v-btn>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <div
                      class="htmlViewPort"
                      v-html="currentlyViewedSupportDoc.htmlContent"
                      @click="handleHTMLClick"
                    ></div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row v-else justify="center">
              <v-col v-if="supportDocs.length > 0">
                <v-row class="d-flex justify-center">
                  <v-col cols="12">
                    <h2 class="text-center font-weight-bold">What would you like to know?</h2>
                  </v-col>
                </v-row>
                <v-row class="d-flex justify-center">
                  <v-col lg="6" md="6" sm="12" id="searchBar">
                    <v-autocomplete
                      :loading="isLoadingSearchResults"
                      :items="searchResults"
                      :search-input.sync="searchQuery"
                      class="mx-4"
                      flat
                      rounded
                      no-filter
                      hide-details
                      height="90"
                      label="Search"
                      prepend-inner-icon="mdi-magnify"
                      solo-inverted
                      attach="#searchBar"
                      @focus="handleInputFocus"
                    >
                      <!-- custom item slot so we can display icons -->
                      <template v-slot:item="{ item }">
                        <v-list-item
                          @click="handleDocClick(item.supportDocId, undefined, 'fromsearch')"
                        >
                          <v-list-item-content>
                            <v-list-item-title
                              ><v-icon>{{ getSearchResultIcon(item.resourceType) }}</v-icon>
                              {{ item.title }}</v-list-item-title
                            >
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-expansion-panels v-model="openTab" accordion class="overflow-y-auto">
                      <v-expansion-panel
                        v-for="supportDocHeader in supportDocSectionHeaders"
                        :key="`support_header_${supportDocHeader}`"
                      >
                        <v-expansion-panel-header @click="handleExpandClick(supportDocHeader)">
                          <div class="supportDocHeader text-left">
                            <h3>{{ supportDocHeader }}</h3>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content
                          :class="{
                            expansionMobile: isMobileView,
                            isQuickLinksExpansion: supportDocHeader == 'Other',
                          }"
                        >
                          <div
                            v-for="supportDoc in supportDocSectionMap[supportDocHeader]"
                            class="supportDocTitleClass"
                            :class="{ isMobile: isMobileView }"
                            :key="`support_title_${supportDoc.title}`"
                            @click="
                              supportDocHeader == 'Other'
                                ? handleDocClick(supportDoc.supportDocId, undefined, 'quicklinks')
                                : handleDocClick(supportDoc.supportDocId, undefined, 'fromlist')
                            "
                          >
                            <div v-if="supportDocHeader == 'Other'">
                              <div
                                v-if="supportDoc.properties.iconBGColor"
                                class="quickLinkBG"
                                :style="{
                                  backgroundColor:
                                    supportDoc.properties.iconBGColor || 'transparent',
                                }"
                              ></div>
                              <v-icon
                                x-large
                                :style="{
                                  color: supportDoc.properties.iconColor || 'rgba(0, 0, 0, .54)',
                                }"
                                >{{ supportDoc.properties.icon }}</v-icon
                              >
                            </div>
                            <div
                              :class="{ 'ml-4': supportDocHeader == 'Other' }"
                              class="supportDocTitleDiv"
                            >
                              {{ supportDoc.title }}
                            </div>
                          </div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
    <LoggingBeacon />
  </v-app>
</template>

<script>
import debounce from 'lodash.debounce'
import LoggingBeacon from 'component-logging-beacon'
import SystemMessage from 'component-system-message'
import SupportDocInterfaceFactory from 'bs-interface-support-doc'
import Parameter from 'parameter-mixin'

export default {
  name: 'SupportDocs',
  mixins: [Parameter],
  components: {
    SystemMessage,
    LoggingBeacon,
  },
  props: {
    dataset: {
      type: DOMStringMap,
    },
  },
  data() {
    return {
      supportHistory: [],
      haveSupportDocsLoaded: false,
      supportDocs: [],
      systemMessage: {
        isShownImmediately: false,
        type: 'success',
        message: '',
      },
      currentlyViewedSupportDocId: undefined,
      currentlyViewedSupportDocIsLoading: false,
      currentlyViewedSupportDoc: undefined,
      currentlyViewedSource: undefined,
      backWasJustClicked: false,
      scrollToHeader: undefined,
      searchQuery: '',
      searchResults: [],
      isLoadingSearchResults: false,
      openTab: 0,
      isModal: false,
      isMobileSize: false,
    }
  },
  async beforeMount() {
    // ensure that if the URL contains ?supportTitle=<something>&header=<something> that we load
    // the support doc for that something (when loaded in iframe)
    const url = new URL(window.location.href)
    const supportTitle = url.searchParams.get('supportTitle')
    const header = url.searchParams.get('header')
    if (this.adacadoDataset?.isModal) {
      this.isModal = true
    }
    this.supportDocInterface
      .getAllSupportDocs()
      .then(supportDocs => {
        this.supportDocs = supportDocs
        // convert all quick links to have a section of 'Other' so they are grouped together
        this.supportDocs.forEach(supportDoc => {
          if (supportDoc.section == 'Quick Links') {
            supportDoc.section = 'Other'
          }
        })
        this.haveSupportDocsLoaded = true
        if (supportTitle) {
          // convert the supportDOc.title to an all lowercase, hyphenated string
          const supportDoc = supportDocs.find(supportDoc => {
            return supportDoc.title.toLowerCase().replace(/ /g, '-') == supportTitle
          })
          if (supportDoc) {
            this.scrollToHeader = header
            this.currentlyViewedSupportDocId = supportDoc.supportDocId
            this.currentlyViewedSource = 'fromurl'
          }
        }
      })
      .catch(() => {
        this.$emit('setSystemMessage', {
          isShownImmediately: true,
          type: 'error',
          message:
            'Oops, we encountered a problem loading the support docs, please try again later.',
        })
      })
  },
  mounted() {
    this.guibus_publish('dispatch:beacon', {
      eventId: `supportDocs-page-viewed`,
    })
    // add a resize handler
    window.addEventListener('resize', () => {
      this.handleResize()
    })
    this.handleResize()
  },
  computed: {
    supportDocInterface() {
      return SupportDocInterfaceFactory()
    },
    supportDocSectionMap() {
      // sorted alphabetically
      return this.supportDocs.reduce((acc, supportDoc) => {
        if (!acc[supportDoc.section]) {
          acc[supportDoc.section] = []
        }
        acc[supportDoc.section].push(supportDoc)
        acc[supportDoc.section].sort((a, b) => a.title.localeCompare(b.title))
        return acc
      }, {})
    },
    supportDocSectionHeaders() {
      return Object.keys(this.supportDocSectionMap)
        .map(header => {
          return header
        })
        .sort((a, b) => {
          if (a == 'Other') {
            return 1
          }
          if (b == 'Other') {
            return -1
          }
          return a.localeCompare(b)
        })
    },
    isMobileView() {
      return this.isMobileSize
    },
  },
  watch: {
    searchQuery() {
      this.handleSearch()
    },
    currentlyViewedSupportDocId(newVal) {
      if (newVal) {
        if (!this.backWasJustClicked) {
          // get the support doc title for the currently viewed support doc for the tracking event
          const supportDocTitle = this.supportDocs.find(
            supportDoc => supportDoc.supportDocId == newVal
          )?.title
          this.guibus_publish('dispatch:beacon', {
            eventId: `supportDocs-view_doc_${
              this.currentlyViewedSource
            }_${this.formatStringForTracking(supportDocTitle)}-click`,
          })
        }
        this.currentlyViewedSupportDocIsLoading = true
        this.supportDocInterface
          .getSupportDoc(newVal)
          .then(supportDoc => {
            this.currentlyViewedSupportDoc = supportDoc
            this.currentlyViewedSupportDocIsLoading = false
            // always scroll back to the top of the support doc when a new one is loaded
            const scrollableContainer = document.querySelector('.htmlViewPort')
            scrollableContainer?.scrollTo({ top: 0 })
            if (this.scrollToHeader) {
              this.$nextTick(() => {
                setTimeout(() => {
                  var headerElement
                  if (this.scrollToHeader.includes('-')) {
                    // support URL called with a header to scroll to:
                    // the headers are not found by their title, but by their id, so we need to scroll to
                    // get the header id from the support doc html content by finding the header that matches the title.
                    // the header as it comes in the URL is lowercase and hyphenated, so we need to convert all header text
                    // content to lowercase and hyphenated to match.
                    const headers = document.querySelectorAll(
                      '.htmlViewPort h1, .htmlViewPort h2, .htmlViewPort h3, .htmlViewPort h4, .htmlViewPort h5, .htmlViewPort h6'
                    )
                    headers.forEach(header => {
                      if (
                        header.textContent
                          .trim()
                          .toLowerCase()
                          .replace(/ /g, '-') == this.scrollToHeader
                      ) {
                        headerElement = header
                      }
                    })
                  } else {
                    const headerId = this.scrollToHeader.replace('#', '')
                    headerElement = document.getElementById(headerId)
                  }
                  if (headerElement) {
                    const scrollableContainer = document.querySelector('.htmlViewPort')
                    scrollableContainer.scrollTo({
                      top: headerElement.offsetTop - scrollableContainer.offsetTop,
                      behavior: 'smooth',
                    })
                  }
                }, 600)
              })
            }
          })
          .catch(() => {
            this.$emit('setSystemMessage', {
              isShownImmediately: true,
              type: 'error',
              message:
                'Oops, we encountered a problem loading the support doc, please try again later.',
            })
          })
      }
      this.backWasJustClicked = false
    },
  },
  methods: {
    handleResize() {
      this.isMobileSize = window.innerWidth < 1209
    },
    handleExpandClick(header) {
      this.guibus_publish('dispatch:beacon', {
        eventId: `supportDocs-expand_section_button_${this.formatStringForTracking(header)}-click`,
      })
    },
    waitForElement(selector, callback, timeout = 5000) {
      const observer = new MutationObserver((mutations, obs) => {
        const element = document.querySelector(selector)
        if (element) {
          obs.disconnect() // Stop observing once found
          callback(element)
        }
      })
      observer.observe(document.body, { childList: true, subtree: true })
      setTimeout(() => observer.disconnect(), timeout)
    },
    handleInputFocus() {
      this.waitForElement('#searchBar .v-menu__content', el => {
        setTimeout(() => {
          // set the position of this element to directly beneath the search bar
          const searchBar = document.querySelector('#searchBar .v-input')
          el.style.top = `${searchBar.offsetHeight + 144}px`
          el.style.left = `${searchBar.offsetLeft}px`
        }, 10)
      })
    },
    handleCloseClick() {
      this.guibus_publish('cancelSupportDocsModal')
    },
    handleBackClick() {
      this.backWasJustClicked = true
      this.supportHistory.pop()
      this.guibus_publish('dispatch:beacon', {
        eventId: `supportDocs-back_button-click`,
      })
      if (this.supportHistory.length == 0) {
        this.currentlyViewedSupportDocId = undefined
        this.currentlyViewedSupportDoc = undefined
        this.currentlyViewedSource = undefined
        return
      }
      this.currentlyViewedSupportDocId = this.supportHistory[this.supportHistory.length - 1]
    },
    handleDocClick(supportDocId, header, source) {
      this.currentlyViewedSupportDocId = supportDocId
      this.currentlyViewedSource = source
      this.supportHistory.push(supportDocId)
      if (header) {
        this.scrollToHeader = header
      } else {
        this.scrollToHeader = undefined
      }
      this.sendPostMessage(supportDocId, header)
    },
    sendPostMessage(supportDocId, header) {
      // send a postMessage with the support doc title and header so that when the
      // support doc is loaded in the iframe, we can copy paste the URL and it
      // will open the correct support doc
      const supportDoc = this.supportDocs.find(doc => doc.supportDocId == supportDocId)
      if (supportDoc) {
        const supportDocTitle = supportDoc.title
        const supportDocHeader = header || ''
        const message = {
          name: 'support-doc-click',
          supportDocTitle: supportDocTitle,
          header: supportDocHeader,
        }
        window.parent.postMessage(message, '*')
      }
    },
    handleHTMLClick(e) {
      const aTag = e.target.closest('a')
      if (aTag) {
        if (aTag.href.includes('%7B%7B')) {
          e.preventDefault()
          // Decode JSON inside href
          const hrefJSON = JSON.parse(
            decodeURIComponent(aTag.href.split('%7B%7B')[1].replace('%7D%7D', ''))
          )
          this.handleDocClick(hrefJSON.id, hrefJSON.header, 'fromdoc')
        } else if (aTag.href.includes('#h')) {
          const truncatedHref = aTag.href.split('#')[1]
          this.sendPostMessage(this.currentlyViewedSupportDocId, truncatedHref)
        }
      }
    },
    parseHTML(htmlContent) {
      // the htmlContent contains JSON in some of the hrefs where another support doc is linked,
      // so we need to parse the JSON and get the supportDocId to use in the click handler. The href JSON
      // object is surrounded in double curly braces so it looks like href="{{{"supportDocId": 123, "type": "support-doc"}}}".
      // So if any hrefs contain "{{", we need to parse the JSON and replace the href with a click handler.
      if (htmlContent.includes(`{{`)) {
        const parser = new DOMParser()
        const doc = parser.parseFromString(htmlContent, 'text/html')
        const aTags = doc.getElementsByTagName('a')
        for (let i = 0; i < aTags.length; i++) {
          const aTag = aTags[i]
          if (aTag.href.includes(`%7B%7B`)) {
            aTag.href = 'javascript:void(0);'
          }
        }
        return doc.body.innerHTML
      }
    },
    getSearchResultIcon(resourceType) {
      switch (resourceType) {
        case 'document':
          return 'mdi-file-document-outline'
        case 'video':
          return 'mdi-video'
        case 'video playlist':
          return 'mdi-playlist-play'
        default:
          return 'mdi-file-document-outline'
      }
    },
    handleSearch: debounce(function() {
      if (this.searchQuery) {
        this.isLoadingSearchResults = true
        this.guibus_publish('dispatch:beacon', {
          eventId: `supportDocs-search_input_${this.formatStringForTracking(
            this.searchQuery
          )}-submit`,
        })
        this.supportDocInterface
          .getSearchResults(this.searchQuery)
          .then(searchResults => {
            this.searchResults = searchResults
            this.isLoadingSearchResults = false
          })
          .catch(() => {
            this.$emit('setSystemMessage', {
              isShownImmediately: true,
              type: 'error',
              message:
                'Oops, we encountered a problem searching the support docs, please try again later.',
            })
          })
      } else {
        this.searchResults = []
        this.isLoadingSearchResults = false
      }
    }, 600),
    formatStringForTracking(title) {
      // convert the title to lowercase, replace spaces with hyphens, and remove any special characters
      return title
        .toLowerCase()
        .replace(/[\s-]+/g, '_') // Replace spaces and hyphens with underscores
        .replace(/[^a-z0-9_]/g, '') // Remove all other special characters
    },
  },
}
</script>
<style>
.v-dialog__content--active {
  z-index: 9999 !important;
}
</style>

<style lang="scss" scoped>
#supportDocsApp {
  h1,
  h2,
  h3,
  h4,
  h6 {
    font-family: 'Muli', Arial, Helvetica, sans-serif !important;
  }
  h1 {
    font-size: 60px;
  }
}
.closeButton {
  position: absolute;
  right: 12px;
  top: 6px;
  cursor: pointer;
  padding: 10px;
  font-size: 24px;
  font-weight: bold;
  color: #8f8d8d;
  border-radius: 20px;
  &:hover {
    background-color: #e0e0e0;
  }
}
.quickLink {
  cursor: pointer;
  margin-bottom: 10px;
  &:hover {
    background-color: #f0f0f0;
  }
}
.quickLinkBG {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  position: absolute;
  margin-left: -6px;
  margin-top: -3px;
}
.supportDocHeader {
  font-size: 24px;
}
.supportDocTitleClass {
  cursor: pointer;
  padding: 3px;
  break-inside: avoid;
  display: flex;
  align-items: center;
  &:hover {
    background-color: #f0f0f0;
    border-radius: 5px;
  }
  &.isMobile {
    margin-bottom: 28px;
  }
}
.supportDocTitleDiv {
  font-family: 'Source Sans Pro', Arial, Helvetica, sans-serif;
  font-size: 20px;
}
.htmlViewPort {
  overflow-y: auto;
  max-height: 80vh;
  padding: 30px;
}
::v-deep(.v-expansion-panel-content__wrap) {
  overflow-y: auto;
  column-count: 4;
  min-height: 334px;
}
.isQuickLinksExpansion {
  ::v-deep(.v-expansion-panel-content__wrap) {
    min-height: 100px;
  }
}
.expansionMobile {
  ::v-deep(.v-expansion-panel-content__wrap) {
    column-count: 2;
  }
}
</style>
